/**
 * IntensitySelector - Component for selecting research intensity level
 *
 * Shows three options: Quick, Standard, Comprehensive
 * All intensity levels are available to authenticated users with remaining runs.
 * Unauthenticated users can only use quick.
 */

import { Zap, Brain, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import type { ResearchIntensity } from '@/types';
import { INTENSITY_CONFIGS } from '@/types';

interface IntensitySelectorProps {
  value: ResearchIntensity;
  onChange: (intensity: ResearchIntensity) => void;
  /** @deprecated Credit balance is no longer used for gating. Kept for API compat. */
  creditBalance?: number | null;
  /** Whether user has unlimited runs */
  isUnlimited?: boolean;
  /** Whether user is authenticated */
  isAuthenticated?: boolean;
  /** Whether user has remaining runs */
  hasRuns?: boolean;
  disabled?: boolean;
  className?: string;
  /** Compact mode for inline display */
  compact?: boolean;
}

const INTENSITY_ICONS = {
  fast: Zap,
  deep: Brain,
};

/**
 * Check if an intensity level is allowed.
 * All intensities are available to authenticated users with runs.
 * Unauthenticated users can only use quick.
 */
export function isIntensityAllowed(
  intensity: ResearchIntensity,
  _creditBalance: number | null | undefined,
  _isUnlimited: boolean,
  isAuthenticated: boolean
): boolean {
  // Unauthenticated users can only use fast
  if (!isAuthenticated) {
    return intensity === 'fast';
  }
  // All authenticated users can use all intensities
  // (run gating happens at query submission, not intensity selection)
  return true;
}

/**
 * Get the best (highest) available intensity.
 * Authenticated users always get comprehensive as default.
 */
export function getBestAvailableIntensity(
  _creditBalance: number | null | undefined,
  _isUnlimited: boolean,
  isAuthenticated: boolean
): ResearchIntensity {
  if (!isAuthenticated) {
    return 'fast';
  }
  return 'deep';
}

/**
 * Check if user can perform research.
 * @deprecated Use run check instead. Kept for backwards compatibility.
 */
export function hasMinimumCredits(
  _creditBalance: number | null | undefined,
  _isUnlimited: boolean
): boolean {
  // Always return true — run gating is handled by the backend
  return true;
}

export function IntensitySelector({
  value,
  onChange,
  creditBalance,
  isUnlimited = false,
  isAuthenticated = false,
  hasRuns: _hasRuns = true,
  disabled = false,
  className,
  compact = false,
}: IntensitySelectorProps) {
  const intensities: ResearchIntensity[] = ['fast', 'deep'];

  return (
    <div
      className={cn(
        'flex gap-2',
        compact ? 'flex-row' : 'flex-col sm:flex-row',
        className
      )}
    >
      {intensities.map((intensity) => {
        const config = INTENSITY_CONFIGS[intensity];
        const Icon = INTENSITY_ICONS[intensity];
        const isAllowed = isIntensityAllowed(intensity, creditBalance, isUnlimited, isAuthenticated);
        const isSelected = value === intensity;

        const tooltipContent = !isAllowed
          ? 'Create a free account to unlock deeper research modes'
          : config.description;

        return (
          <Tooltip key={intensity} content={tooltipContent} position="top">
            <button
              onClick={() => isAllowed && onChange(intensity)}
              disabled={disabled || !isAllowed}
              className={cn(
                'relative flex items-center gap-2 px-3 py-2 rounded-lg border transition-all',
                compact ? 'flex-1' : 'flex-1 sm:flex-none',
                isSelected
                  ? 'border-verdict-primary bg-verdict-primary/10 text-text-primary'
                  : isAllowed
                    ? 'border-border bg-surface-light/50 text-text-secondary hover:border-border-light hover:text-text-primary'
                    : 'border-border bg-surface/50 text-text-muted cursor-not-allowed',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Icon
                className={cn(
                  'w-4 h-4 flex-shrink-0',
                  isSelected ? 'text-verdict-primary' : ''
                )}
              />
              <span className="font-medium text-sm">{config.label}</span>
              {!isAllowed && (
                <Lock className="w-3 h-3 text-text-muted absolute -top-1 -right-1" />
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Compact inline version for hero input */
export function IntensitySelectorInline({
  value,
  onChange,
  creditBalance,
  isUnlimited = false,
  isAuthenticated = false,
  disabled = false,
  className,
}: Omit<IntensitySelectorProps, 'compact'>) {
  const intensities: ResearchIntensity[] = ['fast', 'deep'];

  return (
    <div className={cn('flex items-center gap-1 bg-surface-light/50 rounded-lg p-1', className)}>
      {intensities.map((intensity) => {
        const config = INTENSITY_CONFIGS[intensity];
        const Icon = INTENSITY_ICONS[intensity];
        const isAllowed = isIntensityAllowed(intensity, creditBalance, isUnlimited, isAuthenticated);
        const isSelected = value === intensity;

        const tooltipContent = !isAllowed
          ? 'Create a free account to unlock deeper research modes'
          : config.description;

        return (
          <Tooltip key={intensity} content={tooltipContent} position="top">
            <button
              onClick={() => isAllowed && onChange(intensity)}
              disabled={disabled || !isAllowed}
              className={cn(
                'relative flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-all',
                isSelected
                  ? 'bg-verdict-primary/20 text-verdict-primary'
                  : isAllowed
                    ? 'text-text-muted hover:text-text-primary hover:bg-surface-raised/50'
                    : 'text-text-muted cursor-not-allowed'
              )}
            >
              <Icon className="w-3 h-3" />
              <span>{config.label}</span>
              {!isAllowed && <Lock className="w-2.5 h-2.5 ml-0.5" />}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
